import React from 'react';
import Icon from 'react-web-vector-icons';
import { Chip } from '@mui/material';
import { returnParsedColumnName } from '../../../utils/columns';
import './SequenceList.scss';

// Wrapper component to make Icon work with MUI Chip deleteIcon
const DeleteIcon = React.forwardRef((props, ref) => (
  <span ref={ref} {...props} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ...props.style }}>
    <Icon name="close" font="AntDesign" color="#000000D9" size={16} />
  </span>
));

const SequenceList = ({ sequences, vendor, dropdownData, onSequenceClear, onSequenceDelete, onAddSequence, selectedSequenceIndex, onFilterDelete, onSequenceEdit }) => {

  const getDropdownValuesByField = (field) => {
    if (!dropdownData) return null;
    switch (field) {
      case 'industry_id':
        return dropdownData.industry;
      case 'aws_subsidiary_id':
        return dropdownData.country.map((c) => c.name);
      case 'hq_id':
        return dropdownData.country;
      case 'employees':
        return dropdownData.companySize;
      case 'aws_segment_id':
        return dropdownData.awsSegment;
      case 'aws_adoption_id':
        return dropdownData.adoption;
      case 'region_id':
        return dropdownData.region;
      case 'microsoft_segment_group_id':
        return dropdownData.segmentGroup;
      case 'microsoft_subsegment_id':
        return dropdownData.subSegment;
      default:
        return null;
    }
  };

  const getValueLabel = (field, value) => {
    const dropdownValues = getDropdownValuesByField(field);
    if (!dropdownValues) return value;

    // Handle array values (multi-select)
    if (Array.isArray(value)) {
      const labels = value.map(v => {
        const item = dropdownValues.find(item => 
          typeof item === 'object' ? `${item.id}` === `${v}` : `${item}` === `${v}`
        );
        return item ? (typeof item === 'object' ? item.name : item) : v;
      });
      return labels;
    }

    // Handle single value
    const item = dropdownValues.find(item => 
      typeof item === 'object' ? `${item.id}` === `${value}` : `${item}` === `${value}`
    );
    return item ? (typeof item === 'object' ? item.name : item) : value;
  };

  const getFilterLabel = (row) => {
    // Skip empty or invalid rows
    if (!row.field || !row.value || (Array.isArray(row.value) && row.value.length === 0)) {
      return '';
    }

    const typeLabels = {
      'exact': 'Include',
      'is_not': 'Exclude',
      'contains': 'Contains',
      'starts_with': 'Starts with',
      'ends_with': 'Ends with'
    };

    const field = returnParsedColumnName(row.field);
    const typePrefix = typeLabels[row.type] || row.type;

    // Format value - handle arrays from multi-select
    // Map value to label for picklist fields
    const valueLabel = getValueLabel(row.field, row.value);
    
    let displayValue = '';
    if (Array.isArray(valueLabel)) {
      // Limit display to first few items
      const items = valueLabel.slice(0, 3);
      displayValue = items.join(', ');
      if (valueLabel.length > 3) {
        displayValue += `, +${valueLabel.length - 3} more`;
      }
    } else {
      displayValue = valueLabel;
    }

    // Build label based on type
    if (typePrefix === '') {
      return `${field}: ${displayValue}`;
    } else if (typePrefix === 'Exclude' || typePrefix === 'Include') {
      return `${typePrefix} ${field}(s): ${displayValue}`;
    } else {
      return `${typePrefix} ${field}: ${displayValue}`;
    }
  };

  return (
    <div className="sequence-list-container">
      {sequences.map((sequence, sequenceIndex) => (
        <div key={sequenceIndex}>
          <div className="sequence-item">
            {sequenceIndex > 0 && (
              <div className="or-badge">OR</div>
            )}

            <div className="sequence-header">
              <span className="sequence-title">
                Sequence {sequenceIndex + 1}
                <span
                  className="sequence-clear-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSequenceClear(sequenceIndex);
                  }}
                >
                  Clear
                </span>
              </span>
              {sequences.length > 1 && (
                <div
                  className="sequence-delete-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSequenceDelete(sequenceIndex);
                  }}
                >
                  <Icon name="close" font="AntDesign" color="#000000D9" size={16} />
                </div>
              )}
            </div>

            <div className="sequence-filters">
              {(() => {
                const validRows = sequence.filter(row => row.field && row.value && !(Array.isArray(row.value) && row.value.length === 0));
                
                return validRows.map((row) => {
                  const label = getFilterLabel(row);
                  if (!label) return null;

                  // If this is the last chip in the sequence, clear the sequence instead of deleting the row
                  const handleDelete = () => {
                    if (validRows.length === 1) {
                      onSequenceClear && onSequenceClear(sequenceIndex);
                    } else {
                      onFilterDelete && onFilterDelete(row.key);
                    }
                  };

                  return (
                    <Chip
                      key={row.key}
                      label={label}
                      onDelete={handleDelete}
                      deleteIcon={<DeleteIcon />}
                      className="filter-chip-mui"
                      sx={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #D3E1ED',
                        borderRadius: '25px',
                        height: '34px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        color: '#365A64',
                        '& .MuiChip-label': {
                          padding: '0 12px',
                        },
                        '& .MuiChip-deleteIcon': {
                          marginRight: '4px',
                          '&:hover': {
                            color: '#142A31',
                          },
                        },
                      }}
                    />
                  );
                });
              })()}

              {onSequenceEdit && (
                <button
                  className="add-filter-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSequenceEdit(sequenceIndex);
                  }}
                >
                  <Icon name="plus" font="AntDesign" color="#FFFFFF" size={16} />
                  <span>Add</span>
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      <button className="add-sequence-btn" onClick={onAddSequence}>
        <Icon name="plus" font="AntDesign" color="#FFFFFF" size={16} />
        <span>Add Sequence</span>
      </button>
    </div>
  );
};

export default SequenceList;
